/**
 * User Activity Trend Chart
 *
 * Displays DAU/WAU/MAU trends over time using a custom SVG chart.
 * Features:
 * - Three lines: DAU, WAU, MAU with distinct colors
 * - Interactive crosshair on hover with tooltip
 * - Period selector (7d, 14d, 30d)
 */

import { useState, useRef, useCallback } from 'react';
import type { DailyActiveUsers } from '../../types';

interface UserActivityTrendChartProps {
  data: DailyActiveUsers[];
  isLoading?: boolean;
  selectedPeriod: number;
  onPeriodChange: (days: number) => void;
}

const PERIODS = [
  { label: '7D', value: 7 },
  { label: '14D', value: 14 },
  { label: '30D', value: 30 },
];

function formatNumber(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// Color scheme for the lines
const LINE_COLORS = {
  dau: '#E30B5C', // Raspberry for DAU
  wau: '#10B981', // Green for WAU
  mau: '#F59E0B', // Amber for MAU
};

// Interactive multi-line chart with crosshair
function InteractiveMultiLineChart({
  data,
  width = 600,
  height = 250,
}: {
  data: DailyActiveUsers[];
  width?: number;
  height?: number;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const padding = { top: 20, right: 20, bottom: 30, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || !data.length) return;

      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left - padding.left;

      // Calculate which data point we're hovering over
      const dataIndex = Math.round((x / chartWidth) * (data.length - 1));
      const clampedIndex = Math.max(0, Math.min(data.length - 1, dataIndex));

      setHoverIndex(clampedIndex);
    },
    [data.length, chartWidth, padding.left]
  );

  const handleMouseLeave = useCallback(() => {
    setHoverIndex(null);
  }, []);

  if (!data.length) return null;

  // Calculate min/max across all series
  const allValues = data.flatMap((d) => [d.dau, d.wau, d.mau]);
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  // Add padding to range for visual clarity
  const paddedMin = Math.max(0, minValue - valueRange * 0.1);
  const paddedMax = maxValue + valueRange * 0.1;
  const paddedRange = paddedMax - paddedMin;

  // Calculate point positions
  const getX = (index: number) =>
    padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
  const getY = (value: number) =>
    padding.top + chartHeight - ((value - paddedMin) / paddedRange) * chartHeight;

  // Generate paths for each series
  const generatePath = (values: number[]) => {
    const points = values.map((value, index) => `${getX(index)},${getY(value)}`);
    return `M ${points.join(' L ')}`;
  };

  const dauPath = generatePath(data.map((d) => d.dau));
  const wauPath = generatePath(data.map((d) => d.wau));
  const mauPath = generatePath(data.map((d) => d.mau));

  // Y-axis labels
  const yLabels = [paddedMax, (paddedMax + paddedMin) / 2, paddedMin].map(
    (value, i) => ({
      value: formatNumber(Math.round(value)),
      y: padding.top + (i * chartHeight) / 2,
    })
  );

  // X-axis labels (show every ~5 days)
  const xLabelIndices: number[] = [];
  const labelInterval = Math.max(Math.floor(data.length / 5), 1);
  for (let i = 0; i < data.length; i += labelInterval) {
    xLabelIndices.push(i);
  }
  // Always include last point
  if (xLabelIndices[xLabelIndices.length - 1] !== data.length - 1) {
    xLabelIndices.push(data.length - 1);
  }

  // Hover data
  const hoverData = hoverIndex !== null ? data[hoverIndex] : null;
  const hoverX = hoverIndex !== null ? getX(hoverIndex) : 0;

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="overflow-visible cursor-crosshair"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Grid lines */}
      <g className="text-gray-200">
        {[0, 1, 2].map((i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + (i * chartHeight) / 2}
            x2={padding.left + chartWidth}
            y2={padding.top + (i * chartHeight) / 2}
            stroke="currentColor"
            strokeDasharray="4"
          />
        ))}
      </g>

      {/* Y-axis labels */}
      <g className="text-xs fill-gray-500">
        {yLabels.map((label, i) => (
          <text key={i} x={padding.left - 8} y={label.y + 4} textAnchor="end">
            {label.value}
          </text>
        ))}
      </g>

      {/* X-axis labels */}
      <g className="text-xs fill-gray-500">
        {xLabelIndices.map((idx) => (
          <text
            key={idx}
            x={getX(idx)}
            y={height - 8}
            textAnchor="middle"
          >
            {data[idx] ? formatDate(data[idx].date) : ''}
          </text>
        ))}
      </g>

      {/* MAU line (bottom - amber) */}
      <path
        d={mauPath}
        fill="none"
        stroke={LINE_COLORS.mau}
        strokeWidth={2}
        opacity={0.9}
      />

      {/* WAU line (middle - green) */}
      <path
        d={wauPath}
        fill="none"
        stroke={LINE_COLORS.wau}
        strokeWidth={2}
        opacity={0.9}
      />

      {/* DAU line (top - indigo) */}
      <path
        d={dauPath}
        fill="none"
        stroke={LINE_COLORS.dau}
        strokeWidth={2}
        opacity={0.9}
      />

      {/* Hover crosshair and tooltip */}
      {hoverIndex !== null && hoverData && (
        <>
          {/* Vertical dashed line */}
          <line
            x1={hoverX}
            y1={padding.top}
            x2={hoverX}
            y2={padding.top + chartHeight}
            stroke="#6B7280"
            strokeWidth={1}
            strokeDasharray="4 2"
          />

          {/* Hover points */}
          <circle
            cx={hoverX}
            cy={getY(hoverData.dau)}
            r={5}
            fill={LINE_COLORS.dau}
            stroke="white"
            strokeWidth={2}
          />
          <circle
            cx={hoverX}
            cy={getY(hoverData.wau)}
            r={5}
            fill={LINE_COLORS.wau}
            stroke="white"
            strokeWidth={2}
          />
          <circle
            cx={hoverX}
            cy={getY(hoverData.mau)}
            r={5}
            fill={LINE_COLORS.mau}
            stroke="white"
            strokeWidth={2}
          />

          {/* Tooltip */}
          <g>
            {/* Tooltip background */}
            <rect
              x={hoverX - 60}
              y={padding.top - 10}
              width={120}
              height={90}
              rx={4}
              fill="white"
              stroke="#E5E7EB"
              strokeWidth={1}
              filter="drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))"
            />
            {/* Date */}
            <text
              x={hoverX}
              y={padding.top + 8}
              textAnchor="middle"
              className="text-xs fill-gray-500"
              style={{ fontSize: '11px' }}
            >
              {formatDate(hoverData.date)}
            </text>
            {/* DAU */}
            <text
              x={hoverX}
              y={padding.top + 28}
              textAnchor="middle"
              style={{ fontSize: '12px', fill: LINE_COLORS.dau }}
            >
              DAU: {formatNumber(hoverData.dau)}
            </text>
            {/* WAU */}
            <text
              x={hoverX}
              y={padding.top + 46}
              textAnchor="middle"
              style={{ fontSize: '12px', fill: LINE_COLORS.wau }}
            >
              WAU: {formatNumber(hoverData.wau)}
            </text>
            {/* MAU */}
            <text
              x={hoverX}
              y={padding.top + 64}
              textAnchor="middle"
              style={{ fontSize: '12px', fill: LINE_COLORS.mau }}
            >
              MAU: {formatNumber(hoverData.mau)}
            </text>
          </g>
        </>
      )}
    </svg>
  );
}

export function UserActivityTrendChart({
  data,
  isLoading,
  selectedPeriod,
  onPeriodChange,
}: UserActivityTrendChartProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 w-48 bg-gray-200 rounded mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  // Get latest values
  const latestData = data.length > 0 ? data[data.length - 1] : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Active User Trends
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Daily, Weekly, and Monthly active users
          </p>
        </div>

        {/* Period selector */}
        <div className="flex gap-1">
          {PERIODS.map((period) => (
            <button
              key={period.value}
              onClick={() => onPeriodChange(period.value)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                selectedPeriod === period.value
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Current values */}
      {latestData && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-primary-50 rounded-lg">
            <p className="text-xs text-primary-600 uppercase tracking-wide font-medium">
              DAU
            </p>
            <p className="text-2xl font-bold text-primary-700">
              {formatNumber(latestData.dau)}
            </p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600 uppercase tracking-wide font-medium">
              WAU
            </p>
            <p className="text-2xl font-bold text-green-700">
              {formatNumber(latestData.wau)}
            </p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-600 uppercase tracking-wide font-medium">
              MAU
            </p>
            <p className="text-2xl font-bold text-amber-700">
              {formatNumber(latestData.mau)}
            </p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="mb-4">
        {data.length > 0 ? (
          <InteractiveMultiLineChart data={data} width={600} height={250} />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No activity data available
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-0.5"
            style={{ backgroundColor: LINE_COLORS.dau }}
          />
          <span className="text-gray-600">Daily Active (DAU)</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-0.5"
            style={{ backgroundColor: LINE_COLORS.wau }}
          />
          <span className="text-gray-600">Weekly Active (WAU)</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-0.5"
            style={{ backgroundColor: LINE_COLORS.mau }}
          />
          <span className="text-gray-600">Monthly Active (MAU)</span>
        </div>
      </div>
    </div>
  );
}

export default UserActivityTrendChart;
